import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import classNames from 'classnames';
import { FloatingPopup } from '@nx-frontend/shared/ui/floating-popup';
import { $note } from '@nx-frontend/shared/store/note';
import { $notesTags, $workspaceTags, addTagToNoteEvent } from '@nx-frontend/shared/store/tags';

const DROPDOWN_MAX_SHOWN_TAGS = 10;

enum SpecialItemType {
  CREATE_TAG,
  TAG_ALREADY_ADDED,
  NO_TAGS_YET,
  NO_AVAILABLE_TAGS,
  INVALID_TAG_NAME,
}

function TagsDropdownItem(props: {
  content: string | ReactElement;
  action?: () => void;
  style?: React.CSSProperties;
  classNames?: string[];
}) {
  return (
    <div
      className={classNames('flex items-center px-4', ...(props.classNames ?? []))}
      style={props.style}
      onClick={props.action}
    >
      <span className="truncate">{props.content}</span>
    </div>
  );
}

export function TagsSelectPopup(props: {
  show: boolean;
  onClose: () => void;
  targetRef: React.RefObject<HTMLElement>;
}) {
  const [currentNote, currentWorkspaceTags, notesTags] = useUnit([$note, $workspaceTags, $notesTags]);

  const t = useTranslations('webnotes');

  const [addTagToNote] = useEvent([addTagToNoteEvent]);

  const currentNoteTags = useMemo(() => {
    return notesTags.notes[currentNote.globalId]?.tags ?? [];
  }, [notesTags, currentNote]);

  const [filter, setFilter] = useState<string>('');
  const filteredTags = useMemo(() => {
    return (currentWorkspaceTags.tags ?? [])
      .filter((tag) => !currentNoteTags.includes(tag.name))
      .filter((tag) => tag.name.toLowerCase().startsWith(filter.trim().toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [currentWorkspaceTags, currentNoteTags, filter]);

  const getSpecialItemType: () => SpecialItemType | null = useCallback(() => {
    const tagName = filter.trim();
    const filterIsEmpty = filter.length === 0;
    const filterOnlySpaces = filter.trim().length === 0;
    const tagAlreadyAdded = currentNoteTags.some((tag) => tag === tagName);
    const enteredTagExists = currentWorkspaceTags.tags.some((tag) => tag.name === tagName);
    const workspaceTagsAmount = currentWorkspaceTags.tags?.length ?? 0;
    const allTagsUsed = workspaceTagsAmount === currentNoteTags.length;

    if (filterIsEmpty) {
      if (workspaceTagsAmount === 0) {
        return SpecialItemType.NO_TAGS_YET;
      }
      if (allTagsUsed) {
        return SpecialItemType.NO_AVAILABLE_TAGS;
      }
      return null;
    }
    if (filterOnlySpaces) {
      if (filteredTags.length === 0) {
        if (workspaceTagsAmount === 0) {
          return SpecialItemType.NO_TAGS_YET;
        }
        return SpecialItemType.NO_AVAILABLE_TAGS;
      }
      return null;
    }
    if (tagAlreadyAdded) {
      if (filteredTags.length === 0) {
        return SpecialItemType.TAG_ALREADY_ADDED;
      }
      return null;
    }
    if (enteredTagExists) {
      return null;
    }
    return SpecialItemType.CREATE_TAG;
  }, [currentNoteTags, currentWorkspaceTags.tags, filter, filteredTags.length]);

  const [inputNode, setInputNode] = useState<HTMLInputElement | null>(null);
  const focusInput = useCallback(() => {
    if (props.show && inputNode) {
      inputNode.focus();
    }
  }, [props.show, inputNode]);
  useEffect(focusInput, [focusInput]);

  const triggerAddingTag = useCallback(
    (tag: string) => {
      const specialItemType = getSpecialItemType();
      const tagName = tag.trim();
      if (tagName.length === 0) {
        return;
      }
      if (specialItemType === null || specialItemType === SpecialItemType.CREATE_TAG) {
        addTagToNote({ noteId: currentNote.globalId, tag: tagName });
        setFilter('');
        focusInput();
      }
    },
    [currentNote, addTagToNote, focusInput, getSpecialItemType]
  );

  const handleCloseDropdown = useCallback(() => {
    triggerAddingTag(filter);
    setFilter('');
    props.onClose();
  }, [props, triggerAddingTag, filter]);

  const tagsListPy = 8;
  const tagsListGap = 4;
  const dropdownItemHeight = 28;
  const calculateListHeight = useCallback((itemsNum: number) => {
    return itemsNum * dropdownItemHeight + (itemsNum - 1) * tagsListGap + 2 * tagsListPy + 2;
  }, []);

  const tagsListItems = useMemo(() => {
    const specialItemTag = getSpecialItemType();
    const items = filteredTags.map((tag) => (
      <TagsDropdownItem
        classNames={['cursor-pointer', 'hover:bg-neutral-100', 'flex-none']}
        style={{ height: dropdownItemHeight }}
        content={tag.name}
        action={() => triggerAddingTag(tag.name)}
      />
    ));
    if (specialItemTag === SpecialItemType.CREATE_TAG) {
      items.push(
        <TagsDropdownItem
          classNames={['cursor-pointer', 'hover:bg-neutral-100', 'flex-none']}
          style={{ height: dropdownItemHeight }}
          content={<span className="text-cyan-900">{t('webnotes-create_tag_list_item', { tagName: filter })}</span>}
          action={() => triggerAddingTag(filter)}
        />
      );
    }
    if (specialItemTag !== null && specialItemTag !== SpecialItemType.CREATE_TAG) {
      items.push(
        <TagsDropdownItem
          classNames={['flex-none']}
          style={{ height: dropdownItemHeight }}
          content={
            <span className="text-neutral-500">
              {specialItemTag === SpecialItemType.TAG_ALREADY_ADDED && t('webnotes-tag_is_already_added')}
              {specialItemTag === SpecialItemType.NO_TAGS_YET && t('webnotes-no_tags_yet')}
              {specialItemTag === SpecialItemType.NO_AVAILABLE_TAGS && t('webnotes-no_available_tags')}
              {specialItemTag === SpecialItemType.INVALID_TAG_NAME && t('webnotes-tag_name_is_invalid')}
            </span>
          }
        />
      );
    }
    return items;
  }, [filteredTags, filter, triggerAddingTag, getSpecialItemType, t]);

  const dropdownHeight = useMemo(() => {
    return calculateListHeight(Math.min(tagsListItems.length, DROPDOWN_MAX_SHOWN_TAGS));
  }, [tagsListItems, calculateListHeight]);

  return (
    <FloatingPopup
      show={props.show}
      targetRef={props.targetRef}
      closeButton={false}
      className={['mt-8 !p-0 !bg-transparent !border-0 !shadow-none !w-[328px] z-[100]']}
      containerClassName={['!p-0 !m-2']}
      align={{ marginLeft: -16 }}
      onClose={handleCloseDropdown}
    >
      <div className="pb-2 px-2">
        <input
          ref={setInputNode}
          type="text"
          placeholder={t('webnotes-add_or_search_tags')}
          className={classNames('w-full h-9 px-4', 'border border-neutral-200 rounded-lg outline-none')}
          onChange={(event) => {
            setFilter(event.target.value);
          }}
          value={filter}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              triggerAddingTag(filter);
            }
          }}
        />
        <div
          className={classNames(
            'w-full overflow-auto mt-1 py-2',
            'text-sm',
            'border border-neutral-200 rounded-lg',
            'bg-white shadow-[0_0_10px_0_rgba(163,168,173,0.3)]',
            'flex flex-col shrink-0'
          )}
          style={{
            height: dropdownHeight,
            gap: tagsListGap,
            padding: `${tagsListPy}px 0 ${tagsListPy}px 0`,
          }}
        >
          {tagsListItems}
        </div>
      </div>
    </FloatingPopup>
  );
}
